import { useEffect, useRef, useState } from 'react';
import type { Incident } from '../../services/incidentsApi';
import { incidentsApi } from '../../services/incidentsApi';
import { planRepresentativeEvidenceAutoPromote } from '../../domain/incidents/groupedEvidenceAutoPromote';
import { groupedEvidenceReviewHasCloudProof } from '../../domain/incidents/groupedEvidenceRepresentativePick';

export type GroupedVerifyEvidenceAutoPromoteState = {
  busy: boolean;
  message: string | null;
  error: string | null;
};

/**
 * When the grouped Evidence review panel opens, queue ultra-high HOT edge uploads
 * so reviewers are not stuck on "Clip pending" with disabled manual buttons.
 */
export function useGroupedVerifyEvidenceAutoPromote(args: {
  incident: Incident | null | undefined;
  enabled?: boolean;
  drawerBusy?: string | null;
  onAfterPromote?: () => void | Promise<void>;
}): GroupedVerifyEvidenceAutoPromoteState {
  const { incident, enabled = true, drawerBusy, onAfterPromote } = args;
  const incidentId = String(incident?.incident_id || '').trim();
  const inFlightRef = useRef<Set<string>>(new Set());
  const [state, setState] = useState<GroupedVerifyEvidenceAutoPromoteState>({
    busy: false,
    message: null,
    error: null,
  });

  useEffect(() => {
    if (!enabled || !incidentId || drawerBusy) return;
    if (groupedEvidenceReviewHasCloudProof(incident)) {
      setState({ busy: false, message: null, error: null });
      return;
    }
    const plan = planRepresentativeEvidenceAutoPromote(incident);
    if (!plan || (!plan.clip && !plan.snapshot)) return;
    // Clip may exist on edge even when cloud metadata has not set clip_available_on_edge yet.
    if (inFlightRef.current.has(incidentId)) return;

    let cancelled = false;
    inFlightRef.current.add(incidentId);
    setState({ busy: true, message: 'Pulling proof from edge (ultra-high priority)…', error: null });

    void (async () => {
      try {
        const res = await incidentsApi.promoteEvidence(incidentId, {
          snapshot: plan.snapshot,
          clip: plan.clip,
          promote_priority: 'ultra_hot',
        });
        if (cancelled) return;
        const parts: string[] = [];
        if (res.promote_snapshot?.ok) parts.push('snapshot');
        if (res.promote_clip?.ok) parts.push('clip');
        if (parts.length) {
          setState({
            busy: false,
            message: `Queued ${parts.join(' and ')} upload from edge. Refreshing…`,
            error: null,
          });
        } else {
          const clipErr = String(res.promote_clip?.error || '').trim();
          const snapErr = String(res.promote_snapshot?.error || '').trim();
          const detail =
            clipErr === 'clip_not_found_on_edge'
              ? 'Video is no longer on the edge device for this occurrence (only snapshot remains).'
              : clipErr === 'enqueue_failed_or_duplicate'
                ? 'Video upload is already queued on the edge — wait a minute and refresh clip status.'
                : snapErr || clipErr
                  ? `Edge could not queue proof (${snapErr || clipErr}).`
                  : 'Edge could not queue proof upload (file may still be encoding on device).';
          setState({
            busy: false,
            message: null,
            error: detail,
          });
        }
        if (onAfterPromote) await onAfterPromote();
      } catch (error: any) {
        if (cancelled) return;
        const apiErr = String(error?.response?.data?.error || '').trim();
        const promoteClip = error?.response?.data?.promote_clip;
        const clipErr =
          typeof promoteClip === 'object' && promoteClip
            ? String((promoteClip as { error?: string }).error || '').trim()
            : '';
        setState({
          busy: false,
          message: null,
          error:
            clipErr === 'clip_not_found_on_edge'
              ? 'Video is no longer on the edge device for this occurrence (only snapshot remains).'
              : apiErr === 'edge_promote_failed' && clipErr
                ? `Could not pull video from edge (${clipErr}).`
                : apiErr
                  || error?.response?.data?.detail
                  || error?.message
                  || 'Failed to pull proof from edge',
        });
      } finally {
        inFlightRef.current.delete(incidentId);
        if (!cancelled) {
          setState((prev) => (prev.busy ? { ...prev, busy: false } : prev));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [drawerBusy, enabled, incident, incidentId, onAfterPromote]);

  return state;
}
